import React, { useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';

import AddControl from './add_control';
import ContactModal from 'bundles/backoffice/contacts/contact_modal';
import CopyModal from 'components/contacts/copy_contact_role_modal';
import Contact from './contact';
import EmptyState from 'bundles/backoffice/contacts/contact_empty_state';
import { filterContactRoles } from './helpers';

import './styles.scss';

export default function ContactsPanelHOC({ ContactFormModal, Container, ControlWrapper }) {
  function ContactsPanel({
    addContactRole,
    addContactRoles,
    allowTagging,
    askForEmployer,
    availableRelationships,
    changeContactRole,
    confirmDeletion,
    confirmSharedAttributes,
    contactLabel = 'contact',
    contactRoles,
    containerProps,
    deleteContactRole,
    externalEditing,
    fixedHeight = true,
    forRecord,
    multipleBusinesses,
    parentLabel,
    projectContactRoles,
    readOnly,
    renderLinks,
    showAllExcept,
    showEmployer = true,
    onContactSave,
  }) {
    containerProps = containerProps || {};
    containerProps.className = classNames('backoffice-contacts-panel mb-4', containerProps.className, {
      grow: !fixedHeight,
    });

    containerProps.controls = [];

    const [showContactModal, setShowContactModal] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);

    availableRelationships = availableRelationships.sort((a, b) => a.label.localeCompare(b.label));
    const relationshipTypes = availableRelationships.map(relationship => relationship.value);

    const filterOptions = {};
    if (showAllExcept) {
      filterOptions.except = showAllExcept;
    } else {
      filterOptions.only = relationshipTypes;
    }

    const filteredContactRoles = filterContactRoles(contactRoles, filterOptions);

    let filteredProjectContactRoles;
    if (projectContactRoles) {
      filteredProjectContactRoles = filterContactRoles(projectContactRoles, { only: relationshipTypes });

      if (filteredProjectContactRoles.length > 0) {
        containerProps.controls.push(
          <AddControl
            className="mr-3"
            key="copy-from-project"
            label="Copy from project"
            wrapperComponent={ControlWrapper}
            onClick={() => setShowCopyModal(true)}
          />
        );
      }
    }

    containerProps.controls.push(
      <AddControl
        key="add-contact"
        label="Add contact"
        wrapperComponent={ControlWrapper}
        onClick={() => setShowContactModal(true)}
      />
    );

    if (filteredContactRoles.length === 0) {
      containerProps.children = <EmptyState contactLabel={contactLabel} />;
    } else {
      containerProps.children = filteredContactRoles.map((contactRole, index) => (
        <Contact
          confirmDeletion={confirmDeletion}
          contactRole={contactRole}
          externalEditing={externalEditing}
          formModalComponent={ContactFormModal}
          forRecord={forRecord}
          key={index}
          readOnly={readOnly}
          renderLinks={renderLinks}
          showEmployer={showEmployer}
          onReplace={changeContactRole}
          onDelete={deleteContactRole}
          onSave={onContactSave}
        />
      ));
    }

    return <React.Fragment>
      <Container {...containerProps} />

      <ContactModal
        addContactRole={addContactRole}
        allowTagging={allowTagging}
        askForEmployer={askForEmployer}
        availableContactRelationships={availableRelationships}
        confirmSharedAttributes={confirmSharedAttributes}
        contactLabel={contactLabel}
        forRecord={forRecord}
        multipleBusinesses={multipleBusinesses}
        parentLabel={parentLabel}
        show={showContactModal}
        onClose={() => setShowContactModal(false)}
      />

      <CopyModal
        contactLabel={contactLabel}
        projectContactRoles={filteredProjectContactRoles}
        show={showCopyModal}
        onAddContactRoles={addContactRoles}
        onClose={() => setShowCopyModal(false)}
      />
    </React.Fragment>;
  }

  ContactsPanel.propTypes = {
    addContactRole: PropTypes.func.isRequired,
    addContactRoles: PropTypes.func.isRequired,
    allowTagging: PropTypes.bool,
    askForEmployer: PropTypes.bool,
    availableRelationships: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string.isRequired,
    })).isRequired,
    confirmDeletion: PropTypes.bool,
    confirmSharedAttributes: PropTypes.bool,
    contactLabel: PropTypes.string,
    contactRoles: PropTypes.arrayOf(PropTypes.shape({
      contact: PropTypes.object,
      _destroy: PropTypes.bool,
    })).isRequired,
    changeContactRole: PropTypes.func.isRequired,
    containerProps: PropTypes.object,
    deleteContactRole: PropTypes.func.isRequired,
    externalEditing: PropTypes.bool,
    fixedHeight: PropTypes.bool,
    forRecord: PropTypes.object,
    multipleBusinesses: PropTypes.bool,
    parentLabel: PropTypes.string,
    projectContactRoles: PropTypes.array,
    readOnly: PropTypes.bool,
    renderLinks: PropTypes.bool,
    required: PropTypes.bool,
    showAllExcept: PropTypes.arrayOf(PropTypes.string),
    showEmployer: PropTypes.bool,
    onContactSave: PropTypes.func,
  };

  return ContactsPanel;
}
